import Venue from './Venue'
import BookingType from './BookingType'
import { BookingTime } from '../services/TimesProvider'
import { Tag, TagType, BookingDetails, MarketingPreferenceInterface, MarketingPreference } from '../services/BookingDetailsProvider'
import Package from './Package'
import Offer from './Offer'
import DateService from '../services/DateService'

export class User {
    firstName: string = ''
    lastName: string = ''
    email: string = ''
    phone: string = ''
    dob: string = ''
    pointsBalance: number | null = null
    loggedIn: boolean

    constructor (data: any = {}) {
        this.firstName = data.first_name || ''
        this.lastName = data.last_name || ''
        this.email = data.email || ''
        this.phone = data.phone || ''
        this.dob = data.dob || ''
        this.pointsBalance = data.points_balance || null
        this.loggedIn = data.logged_in || false
    }

    toQuery (): { [index in string]: any } {
        let query: { [index in string]: any } = {
            first_name: this.firstName,
            last_name: this.lastName,
            email: this.email,
            phone: this.phone,
            dob: this.dob
        }

        return query
    }
}

export enum BookingStatus {
    received = 'received',
    confirmed = 'confirmed'
}

export enum ContinueParameter {
    deposit = 'deposit',
    preorder = 'preorder'
}

export class MarketingPreferences {
    data: MarketingPreference[]

    constructor (data: MarketingPreference[] = []) {
        this.data = data
    }

    toQuery (): { [index: number]: string } {
        return this.data.reduce(
            (preferences: string[], preference: MarketingPreference) => {
                if (preference.checked) {
                    preferences.push(preference.id);
                }

                return preferences;
            }, []
        )
    }
}

export class Marketing {
    dmnPermission: boolean = false
    preferences: MarketingPreferences = new MarketingPreferences

    toQuery(): { [index: string]: any } {
        return {
            partner_data_permission: this.dmnPermission,
            marketing_preferences: this.preferences.toQuery()
        }
    }
}

export class CustomField {
    label: string = ''
    value: string = ''
}

export default class Booking {
    venue: Venue | null = null
    pax: Number | null = null
    date: Date | null = null
    type: BookingType | null = null
    times: BookingTime | null = null
    specialRequest: string | null = null
    user: User = new User()
    package: Package | null = null
    offer: Offer | null = null
    continueParameter: ContinueParameter | null = null
    policyConfirm: boolean = false
    marketing: Marketing = new Marketing()
    customField: CustomField | null = null

    tags: any = {}
    tagFields: Tag[] = []

    get hasSpecialRequest (): boolean {
        return this.specialRequest !== '' && this.specialRequest !== null
    }

    toQuery (): { [index in string]: any } {
        let query: { [index in string]: any } = {
            widget_version: 2
        }

        if (this.venue) {
            query.venue_id = this.venue.id
            query.venue_group = this.venue.venueGroupId
        }

        if (this.type) query.type = this.type.id
        if (this.pax) query.num_people = this.pax
        if (this.date) query.date = DateService.formatDateForAPI(this.date)
        if (this.times) {
            query.time = this.times.getStartTimeForAPI()
            query.duration = this.times.duration
        }

        if (this.hasSpecialRequest) query.notes = this.specialRequest
        if (this.offer) query.offer = this.offer
        if (this.hasContinueParameter) query.continue = this.continueParameter

        if (this.hasTagFields) {
            query.tags = this.tagsToQuery()
        }

        query.policy_confirm = this.policyConfirm

        if (this.user) {
            const userParams = this.user.toQuery()

            // TODO: IE doesn't support Object.assign() and Babel isn't transpiling it.
            for (var attrname in userParams) {
                query[attrname] = userParams[attrname];
            }
        }

        if (this.customField) {
            query.custom_field_label = this.customField.label
            query.custom_field_value = this.customField.value
        }

        const marketingParams = this.marketing.toQuery()

        for (var attrname in marketingParams) {
            query[attrname] = marketingParams[attrname]
        }

        return query
    }

    tagsToQuery () {
        let tags: any[] = []
        let val

        this.tagFields.forEach((tagField: any) => {
            val = this.getValueForTag(tagField)

            if (val !== null) {
                tags.push({
                    id: tagField.id,
                    value: val,
                    label: tagField.label,
                    type: tagField.type
                })
            }
        })

        return tags
    }

    getValueForTag (tagField: any): string[] | string | null {
        const val = this.tags[tagField.id]
        let finalVal

        if (val === undefined || val === '') {
            return null
        }

        return val
    }

    setContinueParameter (details: BookingDetails): this {
        if (details.hasMenus) {
            this.continueParameter = ContinueParameter.preorder
            return this
        }

        if (details.cardDetailsRequired) {
            this.continueParameter = ContinueParameter.deposit
            return this
        }

        return this
    }

    get hasTagFields (): boolean {
        return this.tagFields.length > 0
    }

    get hasContinueParameter (): boolean {
        return this.continueParameter !== null
    }
}

export class SentBooking extends Booking {
    reference: string
    user: User

    offer: Offer | null = null
    additionalNotes: string | null = null
    customFieldLabel: string | null = null
    customFieldValue: string | null = null

    constructor (data: any) {
        super()

        this.reference = data.reference
        this.user = new User(data.user)

        if (data.offer) {
            this.offer = new Offer(data.offer)
        }
    }

    get hasOfferApplied (): boolean {
        return !!this.offer
    }

    get hasCustomField (): boolean {
        return !!this.customFieldLabel
    }

    get hasAdditionalNotes (): boolean {
        return !!this.hasAdditionalNotes
    }
}



// WEBPACK FOOTER //
// ./src/models/Booking.ts